import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import Layout from "./components/layout/Layout";
import ScrollToTopOnNavigation from "./components/ScrollToTopOnNavigation";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Tickets from "./pages/Tickets";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import AdminExport from "./pages/AdminExport";
import TicketScanner from "./pages/TicketScanner";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/ResetPassword";
import MaintenancePage from "./pages/MaintenancePage";
import CookieConsentBanner, {
  CookieSettingsButton,
} from "./components/CookieConsentBanner";

// Maintenance Mode Configuration
// Set VITE_MAINTENANCE_MODE=true in .env to enable maintenance mode
// In localhost, the site is always accessible (for development)
const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === "true";
const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname.includes("192.168."));

// Privacy and Cookie Policy pages
const PrivacyPolicy: React.FC = () => {
  const { t } = useLanguage();
  const p = t.privacyPolicyPage;

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gray-50">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto card p-8">
          <h1 className="font-display text-3xl font-bold text-romanian-blue mb-6">
            {p.title}
          </h1>
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-500 italic">{p.lastUpdate}</p>

            <h2>{p.s1Title}</h2>
            <p>{p.s1Text}</p>

            <h2>{p.s2Title}</h2>
            <p>{p.s2Intro}</p>
            <h3>{p.s2aTitle}</h3>
            <ul>
              <li>{p.s2a1}</li>
              <li>{p.s2a2}</li>
              <li>{p.s2a3}</li>
              <li>{p.s2a4}</li>
            </ul>
            <h3>{p.s2bTitle}</h3>
            <ul>
              <li>{p.s2b1}</li>
              <li>{p.s2b2}</li>
              <li>{p.s2b3}</li>
              <li>{p.s2b4}</li>
              <li>{p.s2b5}</li>
              <li>{p.s2b6}</li>
            </ul>
            <h3>{p.s2cTitle}</h3>
            <p>{p.s2cText}</p>

            <h2>{p.s3Title}</h2>
            <p>{p.s3Intro}</p>
            <ul>
              <li>{p.s3i1}</li>
              <li>{p.s3i2}</li>
              <li>{p.s3i3}</li>
              <li>{p.s3i4}</li>
              <li>{p.s3i5}</li>
              <li>{p.s3i6}</li>
              <li>{p.s3i7}</li>
            </ul>

            <h2>{p.s4Title}</h2>
            <p>{p.s4Text}</p>

            <h2>{p.s5Title}</h2>
            <p>{p.s5Text}</p>

            <h2>{p.s6Title}</h2>
            <p>{p.s6Intro}</p>
            <ul>
              <li>
                <strong>Google Firebase</strong> – {p.s6Firebase}
              </li>
              <li>
                <strong>Stripe, Inc.</strong> – {p.s6Stripe}
              </li>
              <li>
                <strong>SendGrid (Twilio)</strong> – {p.s6SendGrid}
              </li>
            </ul>
            <p>{p.s6NoSell}</p>

            <h2>{p.s7Title}</h2>
            <p>{p.s7Text}</p>

            <h2>{p.s8Title}</h2>
            <p>{p.s8Intro}</p>
            <ul>
              <li>
                <strong>{p.s8r1Label}</strong> – {p.s8r1Text}
              </li>
              <li>
                <strong>{p.s8r2Label}</strong> – {p.s8r2Text}
              </li>
              <li>
                <strong>{p.s8r3Label}</strong> – {p.s8r3Text}
              </li>
              <li>
                <strong>{p.s8r4Label}</strong> – {p.s8r4Text}
              </li>
              <li>
                <strong>{p.s8r5Label}</strong> – {p.s8r5Text}
              </li>
              <li>
                <strong>{p.s8r6Label}</strong> – {p.s8r6Text}
              </li>
              <li>
                <strong>{p.s8r7Label}</strong> – {p.s8r7Text}
              </li>
            </ul>
            <p>{p.s8Contact}</p>

            <h2>{p.s9Title}</h2>
            <p>{p.s9Text}</p>

            <h2>{p.s10Title}</h2>
            <p>{p.s10Text}</p>

            <h2>{p.s11Title}</h2>
            <p>{p.s11Text}</p>
            <p>
              <strong>Email:</strong> info@capevremuri.it
              <br />
              <strong>{p.website}:</strong> capevremuri.it
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CookiePolicy: React.FC = () => {
  const { t } = useLanguage();
  const c = t.cookiePolicyPage;

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gray-50">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto card p-8">
          <h1 className="font-display text-3xl font-bold text-romanian-blue mb-6">
            {c.title}
          </h1>
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-500 italic">{c.lastUpdate}</p>

            <h2>{c.s1Title}</h2>
            <p>{c.s1Text}</p>

            <h2>{c.s2Title}</h2>
            <p>{c.s2Text}</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderCookie}
                    </th>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderProvider}
                    </th>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderPurpose}
                    </th>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderDuration}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2 border-b font-mono text-xs">
                      capevremuri_cookie_consent
                    </td>
                    <td className="px-3 py-2 border-b">Ca Pe Vremuri</td>
                    <td className="px-3 py-2 border-b">
                      {c.consentCookiePurpose}
                    </td>
                    <td className="px-3 py-2 border-b">
                      {c.consentCookieDuration}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 border-b font-mono text-xs">
                      capevremuri_language
                    </td>
                    <td className="px-3 py-2 border-b">Ca Pe Vremuri</td>
                    <td className="px-3 py-2 border-b">
                      {c.langCookiePurpose}
                    </td>
                    <td className="px-3 py-2 border-b">
                      {c.langCookieDuration}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2>{c.s3Title}</h2>
            <p>{c.s3Text}</p>
            <ul>
              <li>{c.s3i1}</li>
              <li>{c.s3i2}</li>
              <li>{c.s3i3}</li>
            </ul>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderStorage}
                    </th>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderProvider}
                    </th>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderPurpose}
                    </th>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderDuration}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2 border-b font-mono text-xs">
                      firebaseLocalStorageDb (IndexedDB)
                    </td>
                    <td className="px-3 py-2 border-b">Google Firebase</td>
                    <td className="px-3 py-2 border-b">
                      {c.firebaseIndexedDbPurpose}
                    </td>
                    <td className="px-3 py-2 border-b">
                      {c.firebaseIndexedDbDuration}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 border-b font-mono text-xs">
                      firebase:authUser (localStorage)
                    </td>
                    <td className="px-3 py-2 border-b">Google Firebase</td>
                    <td className="px-3 py-2 border-b">
                      {c.firebaseLocalStoragePurpose}
                    </td>
                    <td className="px-3 py-2 border-b">
                      {c.firebaseLocalStorageDuration}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2>{c.s4Title}</h2>
            <p>{c.s4Text}</p>
            <ul>
              <li>{c.s4i1}</li>
              <li>{c.s4i2}</li>
              <li>{c.s4i3}</li>
            </ul>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderCookie}
                    </th>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderProvider}
                    </th>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderPurpose}
                    </th>
                    <th className="px-3 py-2 text-left border-b">
                      {c.tableHeaderDuration}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2 border-b font-mono text-xs">
                      __stripe_mid / __stripe_sid
                    </td>
                    <td className="px-3 py-2 border-b">Stripe, Inc.</td>
                    <td className="px-3 py-2 border-b">{c.stripeMidPurpose}</td>
                    <td className="px-3 py-2 border-b">
                      {c.stripeMidDuration}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 border-b font-mono text-xs">
                      m / cid (r.stripe.com)
                    </td>
                    <td className="px-3 py-2 border-b">Stripe, Inc.</td>
                    <td className="px-3 py-2 border-b">
                      {c.stripeTelemetryPurpose}
                    </td>
                    <td className="px-3 py-2 border-b">
                      {c.stripeTelemetryDuration}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm">
              {c.stripeMoreInfo}
              <a
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-romanian-blue hover:underline"
              >
                stripe.com/privacy
              </a>
            </p>

            <h2>{c.s5Title}</h2>
            <ul>
              <li>{c.s5i1}</li>
              <li>{c.s5i2}</li>
              <li>{c.s5i3}</li>
              <li>{c.s5i4}</li>
            </ul>

            <h2>{c.s6Title}</h2>
            <p>{c.s6p1}</p>
            <p>{c.s6p2}</p>

            <h2>{c.s7Title}</h2>
            <p>{c.s7Text}</p>
            <p>
              <strong>Email:</strong> info@capevremuri.it
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// 404 Page
const NotFound: React.FC = () => {
  const { language } = useLanguage();
  const isRo = language === "ro";

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gray-50 flex items-center">
      <div className="container-custom text-center">
        <h1 className="font-display text-6xl font-bold text-romanian-blue mb-4">
          404
        </h1>
        <p className="text-gray-600 text-xl mb-8">
          {isRo ? "Pagina nu a fost găsită" : "Pagina non trovata"}
        </p>
        <a href="/" className="btn-primary">
          {isRo ? "Înapoi la Acasă" : "Torna alla Home"}
        </a>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  // Show maintenance page if maintenance mode is enabled AND not on localhost
  if (isMaintenanceMode && !isLocalhost) {
    return <MaintenancePage />;
  }

  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <ScrollToTopOnNavigation />
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/despre-noi" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/acces" element={<Tickets />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/payment-cancel" element={<PaymentCancel />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/admin/export" element={<AdminExport />} />
              <Route path="/admin/scanner" element={<TicketScanner />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Layout>
          <CookieConsentBanner />
          <CookieSettingsButton />
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;
